import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { ConsolidatedSkill } from '@/services/workersV2API';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import AddWorkExperienceModal from './AddWorkExperienceModal';

type Props = {
  skill: ConsolidatedSkill;
  workerId: number;
  onUpdate: () => void;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'Present';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
};

const formatCreatedAt = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDuration = (months: number | null) => {
  if (!months || months === 0) return 'Less than 1 month';
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''}`;

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (remainingMonths === 0) {
    return `${years} year${years !== 1 ? 's' : ''}`;
  }

  return `${years} year${years !== 1 ? 's' : ''} ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
};

const WorkExperienceTab = ({ skill, workerId, onUpdate }: Props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExperienceId, setEditingExperienceId] = useState<number | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingExperienceId, setDeletingExperienceId] = useState<
    number | null
  >(null);
  const [deleting, setDeleting] = useState(false);

  const workExperiences = skill.skill_request?.work_experiences || [];

  const handleAdd = () => {
    setEditingExperienceId(null);
    setModalOpen(true);
  };

  const handleEdit = (experienceId: number) => {
    setEditingExperienceId(experienceId);
    setModalOpen(true);
  };

  const handleDeleteClick = (experienceId: number) => {
    setDeletingExperienceId(experienceId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingExperienceId) return;

    setDeleting(true);
    try {
      await axiosRubyClient.delete(
        `/api/admin/work_experiences/${deletingExperienceId}`,
      );
      onUpdate();
    } catch (err) {
      console.error('Error deleting work experience:', err);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDeletingExperienceId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeletingExperienceId(null);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingExperienceId(null);
  };

  const handleModalSuccess = () => {
    onUpdate();
  };

  return (
    <>
      <Card>
        <Card.Section>
          <Stack
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Stack gap={8} style={{ alignItems: 'center' }}>
              <Body weight="semibold">Work Experience</Body>
              <Badge label={String(workExperiences.length)} status="neutral" />
            </Stack>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={handleAdd}
            >
              Add
            </Button>
          </Stack>
        </Card.Section>

        {workExperiences.length === 0 ? (
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'center',
                padding: '20px',
              }}
            >
              <Small color="lighter">No work experience added yet</Small>
            </Stack>
          </Card.Section>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Company Name</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Staffing Agency</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workExperiences.map((exp) => (
                  <TableRow key={exp.id}>
                    <TableCell>{exp.id}</TableCell>
                    <TableCell>{exp.company_name}</TableCell>
                    <TableCell>{formatDate(exp.start_date)}</TableCell>
                    <TableCell>{formatDate(exp.end_date)}</TableCell>
                    <TableCell>{formatDuration(exp.duration_months)}</TableCell>
                    <TableCell>
                      <Badge
                        label={exp.is_staffing_agency ? 'Yes' : 'No'}
                        status={exp.is_staffing_agency ? 'theme' : 'neutral'}
                      />
                    </TableCell>
                    <TableCell>{formatCreatedAt(exp.created_at)}</TableCell>
                    <TableCell>
                      <Stack gap={4}>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(exp.id)}
                          title="Edit"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(exp.id)}
                          title="Delete"
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <AddWorkExperienceModal
        open={modalOpen}
        workerId={workerId}
        skillId={skill.skill_id}
        skillRequestId={skill.skill_request?.id}
        experienceId={editingExperienceId}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Work Experience</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this work experience? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WorkExperienceTab;
